import React, { forwardRef } from 'react'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../../../stores/app'
import { BASE_CLASSNAME } from '../constants'
import { DomainNotificationBuilderViewProps } from '../types'
import { useLoadedDomainNotifications } from '../../../../../providers/domain-notifications-provider/core'
import classnames from 'classnames'
import { PageHeader } from '@pushly/aqe/lib/components'
import NotificationBuilder, { NotificationBuilderRef } from '../../../notification-builder'
import { NotificationBuilderViewRightAside } from './notification-builder-view-right-aside'
import { NotificationBuilderViewMain } from './notification-builder-view-main'
import { NotificationBuilderLevel, NotificationBuilderTheme } from '../../../enums'
import { useLoadedDomainNotificationTemplate } from '../../../../../providers/domain-notification-template-provider/core'
import { AppService } from '../../../../../services'
import { PlatformLevel } from '@pushly/aqe/lib/types/platform-level'

type DomainNotificationBuilderViewInternalProps = DomainNotificationBuilderViewProps & {
    levelId: number
    requestedNotifId?: number
    requestedTemplateId?: number
    requestedTestId?: number
}

export const DomainNotificationBuilderViewInternals = forwardRef<
    NotificationBuilderRef,
    DomainNotificationBuilderViewInternalProps
>((props, ref) => {
    const {
        className,
        formType = 'send',
        formMode = 'create',
        isMobile = false,
        isInDrawer = false,
        journey,
        journeyNotificationType,
        requestedNotifId,
        requestedTemplateId,
        requestedTestId,
        hidePageHeader = false,
        hideEstimatedReach = false,
        hidePreview = false,
        hideDevicePreviewAction = false,
        hideInlinePreviewAction = false,
        hideDeliveryChannels = false,
        hideAudienceSelection = false,
        hideTestConfig = false,
        hideTemplateSelector = false,
        hideVariantManager = false,
        hideScheduling = false,
        hideSubmit = false,
        hidePushlyAiToggle = false,
        hidePageAnalysisToggle = false,
        showSendToDeviceInFooter = false,
        showTemplateDetails = false,
        nestedDeliveryChannels = false,
        catalogItemPick = false,
        getParentRoute,
        onSubmit,
        onCancel,
        submitText,
        cancelText,
        builderProps,
    } = props

    const appState = useService(AppState)
    const appSvc = useService(AppService)

    const loadedNotifApi = useLoadedDomainNotifications()
    const loadedTemplateApi = useLoadedDomainNotificationTemplate()

    /*
    DOMAIN controlled props
    */
    const level: PlatformLevel = 'domain'
    /* --- */

    const domain = appState.currentDomain!
    const isInCampaign = !!journey
    const isEditMode = formMode === 'edit'

    const builderTheme = isMobile ? NotificationBuilderTheme.MOBILE : NotificationBuilderTheme.STANDARD
    const builderType = formType === 'named-template' ? 'template' : 'notification'
    const builderLevel = isInCampaign ? NotificationBuilderLevel.CAMPAIGN : NotificationBuilderLevel.DOMAIN

    // DOMAIN level does not currently support readonly variant manager mode
    const variantManagerIsReadOnly = false

    const isRemoteLoading = loadedNotifApi.isLoading() || loadedTemplateApi.isLoading()
    const loadedNotifsPromise = loadedTemplateApi.hasRequestedTemplateId()
        ? loadedTemplateApi.getNotificationsPromise()
        : loadedNotifApi.getNotificationsPromise()

    const hideTemplateSelection =
        hideTemplateSelector || (loadedNotifApi.hasRequestedNotificationId() && !loadedNotifApi.hasSourceTemplateId())

    const pageTitle = isEditMode
        ? builderType === 'notification'
            ? 'Edit Notification'
            : 'Edit Notification Template'
        : builderType === 'notification'
        ? 'Create Notification'
        : 'Create Notification Template'
    const pageTitleAppend =
        builderType === 'notification' && isEditMode && requestedNotifId ? `#${requestedNotifId}` : undefined

    return (
        <div
            className={classnames(
                BASE_CLASSNAME,
                `${BASE_CLASSNAME}__level-${level}`,
                isMobile && `${BASE_CLASSNAME}__mobile`,
                isRemoteLoading && `${BASE_CLASSNAME}__loading`,
                className,
            )}
        >
            {!hidePageHeader && (
                <PageHeader title={pageTitle} append={pageTitleAppend} onTitleSet={appSvc.customizeTabTitle} />
            )}

            <NotificationBuilder
                {...builderProps}
                ref={ref}
                className={classnames(isInDrawer && 'drawer-mode', builderProps?.className)}
                mode={formMode}
                type={builderType}
                theme={builderTheme}
                domain={domain}
                level={builderLevel}
                notifications={builderProps?.notifications ?? loadedNotifsPromise}
                defaultSelectedNotifId={requestedNotifId ?? requestedTemplateId}
                defaultSelectedTemplateId={requestedTemplateId}
            >
                {({
                    builder,
                    dispatchChanges,
                    onInitialVariantInit,
                    validateSubmit,
                    onChannelChange,
                    onTestChange,
                    onAudienceChange,
                    onDistributionChange,
                    onVariantChange,
                    onSelectedTemplateChange,
                    onDetailsChange,
                }) => {
                    return (
                        <>
                            <NotificationBuilderViewRightAside
                                loading={isRemoteLoading}
                                level={level}
                                domain={domain}
                                builder={builder}
                                hideEstimatedReach={hideEstimatedReach}
                                hidePreview={hidePreview}
                                hideDevicePreviewAction={hideDevicePreviewAction}
                                hideInlinePreviewAction={hideInlinePreviewAction}
                                isInDrawer={isInDrawer}
                                dispatchChanges={dispatchChanges}
                                onDistributionChange={onDistributionChange}
                                validateSubmit={validateSubmit}
                                journey={journey}
                                journeyNotificationType={journeyNotificationType}
                            />

                            <NotificationBuilderViewMain
                                level={level}
                                formType={formType}
                                formMode={formMode}
                                loading={isRemoteLoading}
                                domain={domain}
                                builder={builder}
                                builderProps={builderProps}
                                isMobile={isMobile}
                                isInDrawer={isInDrawer}
                                requestedNotifId={requestedNotifId}
                                requestedTemplateId={requestedTemplateId}
                                requestedTestId={requestedTestId}
                                hideDeliveryChannels={hideDeliveryChannels}
                                hideAudienceSelection={hideAudienceSelection}
                                hideTestConfig={hideTestConfig}
                                hideTemplateSelector={hideTemplateSelection}
                                hideVariantManager={hideVariantManager}
                                hideScheduling={hideScheduling}
                                hideSubmit={hideSubmit}
                                hidePushlyAiToggle={hidePushlyAiToggle}
                                hidePageAnalysisToggle={hidePageAnalysisToggle}
                                showSendToDeviceInFooter={showSendToDeviceInFooter}
                                readonlyVariantManager={variantManagerIsReadOnly}
                                showTemplateDetails={showTemplateDetails}
                                nestedDeliveryChannels={nestedDeliveryChannels}
                                dispatchChanges={dispatchChanges}
                                getParentRoute={getParentRoute}
                                onTemplateChange={onSelectedTemplateChange}
                                onVariantChange={onVariantChange}
                                onChannelChange={onChannelChange}
                                onAudienceChange={onAudienceChange}
                                onTestChange={onTestChange}
                                onInitialVariantInit={onInitialVariantInit}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                onTemplateDetailsChange={onDetailsChange}
                                submitText={submitText}
                                cancelText={cancelText}
                                journey={journey}
                                journeyNotificationType={journeyNotificationType}
                                catalogItemPick={catalogItemPick}
                                validateSubmit={validateSubmit}
                            />
                        </>
                    )
                }}
            </NotificationBuilder>
        </div>
    )
})
